/**
 * Which Recent Trades slides should carry real image sources.
 *
 * The carousel renders every slide in one horizontal track and advances one
 * slide at a time, wrapping from the last back to the first. Before Stage 2
 * (2026-10-31) every slide's images had a real `src` from the first render:
 * on the live dashboard that was ~300 image requests at mount (172 pedal
 * thumbnails and dozens of avatars, half of them full-size uploads drawn into
 * a 67 px circle), all competing for bandwidth with the card the visitor is
 * actually looking at. Now only the current slide and its neighbours (the
 * one rotation will show next, and the wrap-around partner) load images, and
 * a slide that has loaded once keeps its images so rotating past it never
 * flashes blank.
 *
 * Pure helpers so the policy is testable without rendering.
 */

/** 1x1 transparent GIF: the `src` of a slide that is not yet allowed to load. */
export const BLANK_IMAGE_SRC = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Indexes within `radius` slides of `current`, wrapping at both ends.
 * @returns {Set<number>}
 */
export function nearSlideIndexes(current, length, radius = 1) {
  const near = new Set();
  if (!Number.isInteger(length) || length <= 0) return near;
  const cur = ((current % length) + length) % length;
  for (let d = -radius; d <= radius; d += 1) {
    near.add((((cur + d) % length) + length) % length);
  }
  return near;
}

/**
 * Mutable record of which slides have been allowed to load. `advance` adds the
 * slides near `current` and returns the full set; `reset` forgets everything
 * (call when the slide list itself changes, since indexes no longer mean the
 * same cards).
 */
export function createRevealedSlides() {
  let revealed = new Set();
  return {
    advance(current, length, radius = 1) {
      for (const i of nearSlideIndexes(current, length, radius)) revealed.add(i);
      return revealed;
    },
    has: (index) => revealed.has(index),
    reset() { revealed = new Set(); },
  };
}
